import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Upload, Trash2, FileSpreadsheet, Eye, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';

const Hisaab = () => {
  const { API } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewRecords, setViewRecords] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [recordsData, setRecordsData] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waMessages, setWaMessages] = useState([]);
  const [expandedUpload, setExpandedUpload] = useState(null);

  const fetchUploads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hisaab/uploads`);
      setUploads(res.data);
    } catch (error) {
      toast.error('Failed to fetch hisaab uploads');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchUploads(); }, [fetchUploads]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an XLSX or CSV file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/hisaab/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded: ${res.data.record_count} records for week ${res.data.week_begin || 'N/A'}`);
      fetchUploads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Delete this upload and all its records?')) return;
    try {
      await axios.delete(`${API}/hisaab/uploads/${uploadId}`);
      toast.success('Upload deleted');
      fetchUploads();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleViewRecords = async (upload) => {
    setViewRecords(upload);
    setViewDialogOpen(true);
    setLoadingRecords(true);
    try {
      const res = await axios.get(`${API}/hisaab/records`, { params: { upload_id: upload.id } });
      setRecordsData(res.data);
    } catch (error) {
      toast.error('Failed to fetch records');
    } finally {
      setLoadingRecords(false);
    }
  };

  const buildWhatsAppMessages = async (uploadId) => {
    try {
      const res = await axios.get(`${API}/hisaab/records`, { params: { upload_id: uploadId } });
      const records = res.data;
      // Group records by driver_id_ext
      const grouped = {};
      for (const r of records) {
        const key = r.driver_id_ext || r.name;
        if (!grouped[key]) grouped[key] = { name: r.name, driver_id_ext: r.driver_id_ext, entries: [] };
        grouped[key].entries.push(r);
      }
      // Build messages - look up mobile from drivers
      const driversRes = await axios.get(`${API}/drivers`);
      const driverMap = {};
      for (const d of driversRes.data) {
        driverMap[d.driver_id] = d.mobile;
      }

      const messages = Object.values(grouped).map((g) => {
        const mobile = driverMap[g.driver_id_ext] || '';
        let text = `Hisaab - ${g.name} (${g.driver_id_ext})\n\n`;
        let totalOS = 0;
        for (const e of g.entries) {
          text += `Vehicle: ${e.vehicle_number}\n`;
          text += `Earnings: ${e.your_earnings}, Trips: ${e.trips}\n`;
          text += `Lease: ${e.total_lease}, Final OS: ${e.final_os}\n\n`;
          totalOS += e.final_os;
        }
        text += `Total Outstanding: ${totalOS.toFixed(2)}`;
        const encoded = encodeURIComponent(text);
        const waLink = mobile ? `https://wa.me/91${mobile.replace(/\D/g, '')}?text=${encoded}` : '';
        return { name: g.name, driver_id_ext: g.driver_id_ext, mobile, entries: g.entries, totalOS, waLink, text };
      });
      setWaMessages(messages);
      setWaDialogOpen(true);
    } catch (error) {
      toast.error('Failed to build WhatsApp messages');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="space-y-6" data-testid="hisaab-section">
      {/* Upload Card */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900">Weekly Hisaab Upload</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Upload weekly driver hisaab XLSX/CSV files. Each upload is stored separately.</p>
            </div>
            <div className="relative">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                id="hisaab-file-input"
                disabled={uploading}
                data-testid="hisaab-file-input"
              />
              <Button
                onClick={() => document.getElementById('hisaab-file-input').click()}
                disabled={uploading}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="upload-hisaab-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Uploading...' : 'Upload Hisaab'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-4 mb-4">
            <p className="text-sm text-slate-600">
              <strong>Required columns:</strong> Week Begin, Vehicle Number, Driver Id, Name, Trips, Your earnings, Payouts,
              Cash collected, Driver subscription charge, Refunds, Toll Incentive, Ola Bank Transfer, Adjustment,
              Bachat Days, Total Lease, OS Settled Amount, Final OS
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : uploads.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No uploads yet. Upload your first hisaab file.</p>
            </div>
          ) : (
            <Table data-testid="uploads-table">
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">File</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Week</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Records</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Uploaded</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploads.map((upload) => (
                  <TableRow key={upload.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100">
                    <TableCell className="font-medium text-sm" data-testid={`upload-file-${upload.id}`}>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                        {upload.filename}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(upload.week_begin)}</TableCell>
                    <TableCell>
                      <span className="inline-flex px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
                        {upload.record_count} rows
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(upload.uploaded_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => handleViewRecords(upload)} data-testid={`view-upload-${upload.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => buildWhatsAppMessages(upload.id)} data-testid={`wa-upload-${upload.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(upload.id)} data-testid={`delete-upload-${upload.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Records Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="view-records-dialog">
          <DialogHeader>
            <DialogTitle>Hisaab Records — {viewRecords?.filename}</DialogTitle>
            <DialogDescription>Week: {formatDate(viewRecords?.week_begin)} — {viewRecords?.record_count} records</DialogDescription>
          </DialogHeader>
          {loadingRecords ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 text-[11px]">
                    <TableHead className="text-xs">Driver ID</TableHead>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Vehicle</TableHead>
                    <TableHead className="text-xs">Trips</TableHead>
                    <TableHead className="text-xs">Earnings</TableHead>
                    <TableHead className="text-xs">Payouts</TableHead>
                    <TableHead className="text-xs">Cash</TableHead>
                    <TableHead className="text-xs">Subscription</TableHead>
                    <TableHead className="text-xs">Total Lease</TableHead>
                    <TableHead className="text-xs">OS Settled</TableHead>
                    <TableHead className="text-xs font-bold">Final OS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recordsData.map((r) => (
                    <TableRow key={r.id} className="text-sm">
                      <TableCell className="font-mono text-xs">{r.driver_id_ext}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.vehicle_number}</TableCell>
                      <TableCell>{r.trips}</TableCell>
                      <TableCell>{r.your_earnings?.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{r.payouts?.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{r.cash_collected?.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{r.driver_subscription_charge?.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{r.total_lease?.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{r.os_settled_amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="font-bold">{r.final_os?.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* WhatsApp Share Dialog */}
      <Dialog open={waDialogOpen} onOpenChange={setWaDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wa-dialog">
          <DialogHeader>
            <DialogTitle>Share Hisaab via WhatsApp</DialogTitle>
            <DialogDescription>
              Click the WhatsApp button to send consolidated hisaab to each driver. Drivers with multiple vehicles are grouped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {waMessages.map((msg, idx) => (
              <Card key={idx} className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{msg.name}</span>
                        <span className="font-mono text-xs text-slate-500">({msg.driver_id_ext})</span>
                        {msg.entries.length > 1 && (
                          <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            {msg.entries.length} vehicles
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Mobile: {msg.mobile || 'Not linked'} | OS: {msg.totalOS.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                      </p>
                      <button
                        className="text-xs text-blue-600 mt-1 flex items-center gap-1"
                        onClick={() => setExpandedUpload(expandedUpload === idx ? null : idx)}
                      >
                        {expandedUpload === idx ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {expandedUpload === idx ? 'Hide preview' : 'Preview message'}
                      </button>
                      {expandedUpload === idx && (
                        <pre className="mt-2 text-xs bg-slate-50 p-3 rounded whitespace-pre-wrap border">{msg.text}</pre>
                      )}
                    </div>
                    {msg.waLink ? (
                      <a href={msg.waLink} target="_blank" rel="noopener noreferrer" data-testid={`wa-send-${msg.driver_id_ext}`}>
                        <Button size="sm" className="bg-green-600 text-white hover:bg-green-700">
                          <MessageCircle className="w-4 h-4 mr-1" /> Send
                        </Button>
                      </a>
                    ) : (
                      <Button size="sm" disabled variant="outline" className="text-slate-400">
                        No mobile
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {waMessages.length === 0 && (
              <p className="text-center text-slate-400 py-4 text-sm">No records found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Hisaab;
